/**
 * useProceedToPayment Hook
 *
 * Initiates payment flow for an order using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import type { Order } from "@/generated/graphql";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const ProceedToPaymentDocument = `
  mutation ProceedToPayment($orderId: ID!) {
    proceedToPayment(orderId: $orderId) {
      id
      status
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseProceedToPaymentResult {
  proceedToPayment: (orderId: string) => Promise<Order>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Proceed to payment for an order.
 *
 * After calling this mutation:
 * - Order status changes to AWAITING_PAYMENT
 * - Cache is automatically invalidated (see client.ts)
 */
export function useProceedToPayment(): UseProceedToPaymentResult {
  const [result, executeMutation] = useMutation<
    { proceedToPayment: Order },
    { orderId: string }
  >(ProceedToPaymentDocument);

  const proceedToPayment = useCallback(
    async (orderId: string): Promise<Order> => {
      try {
        const response = await executeMutation({ orderId });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to proceed to payment"
          );
        }

        if (!response.data?.proceedToPayment) {
          throw new Error("No order returned from server");
        }

        const order = response.data.proceedToPayment;

        errorTracking.addBreadcrumb({
          category: "payment",
          message: "Proceeding to payment",
          data: { orderId: order.id, status: order.status },
        });

        return order;
      } catch (err) {
        const paymentError =
          err instanceof Error ? err : new Error("Failed to proceed to payment");

        errorTracking.captureError(paymentError, {
          tags: { feature: "payment", action: "proceedToPayment" },
          extra: { orderId },
        });

        throw paymentError;
      }
    },
    [executeMutation]
  );

  return {
    proceedToPayment,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
